import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import MobileHeader from "./MobileHeader";
import {
	FiSun,
	FiMoon,
	FiCalendar,
	FiChevronRight,
	FiLogOut,
	FiHome,
} from "react-icons/fi";

interface MenuItem {
	path: string;
	label: string;
	icon: React.ReactNode;
	badge?: string | number;
}

interface DashboardLayoutProps {
	children: React.ReactNode;
	title?: string;
	subtitle?: string;
	menuItems: MenuItem[];
	basePath: string;
	appName: string;
	appDescription?: string;
	logoIcon?: React.ReactNode;
	onLogout?: () => void;
	showHeader?: boolean;
	accentColor?: 'violet' | 'blue';
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
	children,
	title,
	subtitle,
	menuItems,
	basePath,
	appName,
	appDescription,
	logoIcon,
	onLogout,
	showHeader = true,
	accentColor = 'violet',
}) => {
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
	const location = useLocation();
	const { theme, toggleTheme } = useTheme();

	const toggleSidebar = () => {
		setSidebarOpen(!sidebarOpen);
	};

	const isActivePath = (itemPath: string) => {
		const fullPath = itemPath === "" ? basePath : `${basePath}/${itemPath}`;
		const normalizedFullPath = fullPath.endsWith("/")
			? fullPath.slice(0, -1)
			: fullPath;
		const normalizedCurrentPath = location.pathname.endsWith("/")
			? location.pathname.slice(0, -1)
			: location.pathname;

		if (itemPath === "") {
			return (
				normalizedCurrentPath === normalizedFullPath ||
				normalizedCurrentPath === `${normalizedFullPath}/events`
			);
		}

		return normalizedCurrentPath.startsWith(normalizedFullPath);
	};

	const contentMinHeightClass = "min-h-[calc(100vh-5rem)] lg:min-h-screen";

	return (
		<div className="h-screen overflow-hidden transition-colors duration-200">
			{/* Mobile Header - Show only on mobile */}
			<div className="fixed top-0 left-0 right-0 z-40 lg:hidden">
				<MobileHeader onMenuClick={toggleSidebar} isMenuOpen={sidebarOpen} />
			</div>

			{/* Sidebar */}
		<div
			className={`fixed left-0 top-0 bottom-0 z-50 transform ${
				sidebarOpen ? "translate-x-0" : "-translate-x-full"
			} lg:translate-x-0 transition-transform duration-300 ease-in-out ${
				sidebarCollapsed ? "lg:w-20" : "lg:w-64"
			} w-64 lg:inset-y-0 h-screen overflow-hidden`}
		>
				<div className="h-full bg-gray-900 dark:bg-black shadow-xl flex flex-col overflow-hidden">
					{/* Logo Section */}
					<div className="flex items-center justify-between h-20 px-6 border-b border-gray-800 dark:border-gray-700 flex-shrink-0">
						<div className="flex items-center space-x-3">
							<div className={`flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br text-white ${
								accentColor === 'blue' ? 'from-blue-500 to-indigo-600' : 'from-violet-500 to-purple-600'
							}`}>
								{logoIcon || <FiCalendar className="h-6 w-6" />}
							</div>
							{!sidebarCollapsed && (
								<div>
									<h2 className="text-lg font-bold text-white">{appName}</h2>
									{appDescription && (
										<p className="text-xs text-gray-400">{appDescription}</p>
									)}
								</div>
							)}
						</div>
						<button
							onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
							className="hidden lg:block p-1 rounded hover:bg-gray-800 transition-colors"
						>
							<FiChevronRight
								className={`h-5 w-5 text-gray-400 transform transition-transform ${
									sidebarCollapsed ? "" : "rotate-180"
								}`}
							/>
						</button>
					</div>

					{/* Navigation */}
					<nav className="flex-1 px-3 py-4 lg:py-8">
						{/* b+ticket トップへのリンク */}
						<div className="mb-6 pb-4 border-b border-gray-800">
							<a
								href="/"
								className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-all duration-200"
							>
								<FiHome className="h-5 w-5" />
								{!sidebarCollapsed && (
									<span className="text-sm font-medium">b+ticketトップへ</span>
								)}
							</a>
						</div>

						<div className="space-y-1">
							{menuItems.map((item) => {
								const isActive = isActivePath(item.path);
								const fullPath =
									item.path === "" ? basePath : `${basePath}/${item.path}`;

								return (
									<Link
										key={item.path}
										to={fullPath}
										onClick={() => setSidebarOpen(false)}
										className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-all duration-200 group ${
											isActive
												? accentColor === 'blue'
													? "bg-gradient-to-r from-blue-500/20 to-indigo-600/20 text-blue-400"
													: "bg-gradient-to-r from-violet-500/20 to-purple-600/20 text-violet-400"
												: "text-gray-400 hover:text-white hover:bg-gray-800"
										}`}
									>
										<div className="flex items-center space-x-3">
											<div className={`${sidebarCollapsed ? "mx-auto" : ""}`}>
												{item.icon}
											</div>
											{!sidebarCollapsed && (
												<span className="text-sm font-medium">
													{item.label}
												</span>
											)}
										</div>
										{item.badge && !sidebarCollapsed && (
											<span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-500 text-gray-900">
												{item.badge}
											</span>
										)}
									</Link>
								);
							})}
						</div>
					</nav>

					{/* Bottom Actions */}
					<div className="p-3 border-t border-gray-800 dark:border-gray-700 flex-shrink-0">
						<div className="space-y-1">
							{/* Theme Toggle */}
							<button
								onClick={toggleTheme}
								className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-all duration-200"
							>
								<div className="flex items-center space-x-3">
									{theme === "light" ? (
										<FiMoon className="h-5 w-5" />
									) : (
										<FiSun className="h-5 w-5" />
									)}
									{!sidebarCollapsed && (
										<span className="text-sm font-medium">
											{theme === "light" ? "ダークモード" : "ライトモード"}
										</span>
									)}
								</div>
							</button>

							{/* Logout Button */}
							{onLogout && (
								<button
									onClick={onLogout}
									className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-all duration-200"
								>
									<FiLogOut className="h-5 w-5" />
									{!sidebarCollapsed && (
										<span className="text-sm font-medium">ログアウト</span>
									)}
								</button>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Mobile sidebar overlay */}
			{sidebarOpen && (
				<div
					className="fixed inset-0 z-30 bg-black bg-opacity-50 lg:hidden"
					onClick={() => setSidebarOpen(false)}
				/>
			)}

			{/* Main Content */}
		<div
			className={`${
				sidebarCollapsed ? "lg:ml-20" : "lg:ml-64"
			} transition-all duration-300 pt-20 lg:pt-0 h-screen overflow-y-auto`}
		>
				{/* Top Bar - Conditionally rendered */}
				{showHeader && (
					<div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
						<div className="px-4 sm:px-6 lg:px-8 py-4">
							<div className="flex items-center justify-between">
								<div>
									{title && (
										<h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
											{title}
										</h1>
									)}
									{subtitle && (
										<p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
											{subtitle}
										</p>
									)}
								</div>
								<div className="flex items-center space-x-4">
									{/* Additional actions can be added here */}
								</div>
							</div>
						</div>
					</div>
				)}

			{/* Page Content */}
			<div
				className={`${
					showHeader ? "p-4 sm:p-6 lg:p-8" : "p-0"
				} ${contentMinHeightClass}`}
			>
				<div
					className={`${
						showHeader
							? "bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700"
							: "bg-transparent"
					} min-h-full`}
				>
					<div className={`${showHeader ? "p-6" : "p-0"} min-h-full`}>
						{children}
					</div>
				</div>
			</div>
		</div>
	</div>
);
};

export default DashboardLayout;
